/**
 * Map picking + control-point overlay + image placement for Georeference.
 *
 * Follows the Path Profile surface: MapLibre is preferred, OpenLayers canvas
 * is the software-GL fallback. Giro3D (3D-only) returns null so the panel
 * can ask the operator to switch to the 2D map.
 */

import { getMapLibreHostHandle } from '@/engines/maplibre/hostHandle';
import { getCanvasMapHostHandle } from '@/engines/openlayers/hostHandle';
import { tokens } from '@/design';
import type { GeoreferenceCorners, LonLat } from './georeferenceTypes';

const SOURCE_ID = 'geosoft-georeference';
const LINK_LAYER_ID = `${SOURCE_ID}-links`;
const SOURCE_POINT_LAYER_ID = `${SOURCE_ID}-from`;
const TARGET_POINT_LAYER_ID = `${SOURCE_ID}-to`;

export interface GeoreferencePickHandlers {
  readonly onPoint: (lonLat: LonLat) => void;
}

export interface DraftControlPoint {
  readonly id: string;
  readonly source: LonLat | null;
  readonly target: LonLat | null;
}

export interface GeoreferenceDrawSurface {
  readonly engine: 'maplibre' | 'openlayers';
  attach: (handlers: GeoreferencePickHandlers) => (() => void) | null;
  render: (points: readonly DraftControlPoint[]) => void;
  clear: () => void;
}

function controlPointGeoJson(
  points: readonly DraftControlPoint[],
): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];
  for (const point of points) {
    if (point.source) {
      features.push({
        type: 'Feature',
        properties: { role: 'from', id: point.id },
        geometry: { type: 'Point', coordinates: [...point.source] },
      });
    }
    if (point.target) {
      features.push({
        type: 'Feature',
        properties: { role: 'to', id: point.id },
        geometry: { type: 'Point', coordinates: [...point.target] },
      });
    }
    if (point.source && point.target) {
      features.push({
        type: 'Feature',
        properties: { role: 'link', id: point.id },
        geometry: {
          type: 'LineString',
          coordinates: [[...point.source], [...point.target]],
        },
      });
    }
  }
  return { type: 'FeatureCollection', features };
}

function createMapLibreSurface(container: HTMLElement): GeoreferenceDrawSurface | null {
  if (!getMapLibreHostHandle(container)) return null;

  return {
    engine: 'maplibre',
    attach(handlers) {
      const map = getMapLibreHostHandle(container);
      if (!map) return null;
      const canvas = map.getCanvas();
      const previousCursor = canvas.style.cursor;
      canvas.style.cursor = 'crosshair';
      const onClick = (event: { lngLat: { lng: number; lat: number } }) => {
        handlers.onPoint([event.lngLat.lng, event.lngLat.lat]);
      };
      map.on('click', onClick);
      return () => {
        try {
          map.off('click', onClick);
          canvas.style.cursor = previousCursor;
        } catch {
          // Map may already be torn down.
        }
      };
    },
    render(points) {
      const map = getMapLibreHostHandle(container);
      if (!map?.isStyleLoaded()) return;
      const data = controlPointGeoJson(points);
      const source = map.getSource(SOURCE_ID) as
        | { setData: (next: GeoJSON.FeatureCollection) => void }
        | undefined;
      if (source) {
        source.setData(data);
        return;
      }
      map.addSource(SOURCE_ID, { type: 'geojson', data });
      map.addLayer({
        id: LINK_LAYER_ID,
        type: 'line',
        source: SOURCE_ID,
        filter: ['==', ['get', 'role'], 'link'],
        paint: {
          'line-color': tokens.color.semantic.warning,
          'line-width': 2,
          'line-dasharray': [2, 1],
        },
      });
      map.addLayer({
        id: SOURCE_POINT_LAYER_ID,
        type: 'circle',
        source: SOURCE_ID,
        filter: ['==', ['get', 'role'], 'from'],
        paint: {
          'circle-radius': 6,
          'circle-color': tokens.color.semantic.warning,
          'circle-stroke-width': 2,
          'circle-stroke-color': tokens.color.ink.inverse,
        },
      });
      map.addLayer({
        id: TARGET_POINT_LAYER_ID,
        type: 'circle',
        source: SOURCE_ID,
        filter: ['==', ['get', 'role'], 'to'],
        paint: {
          'circle-radius': 6,
          'circle-color': tokens.color.accent.primary,
          'circle-stroke-width': 2,
          'circle-stroke-color': tokens.color.ink.inverse,
        },
      });
    },
    clear() {
      const map = getMapLibreHostHandle(container);
      if (!map) return;
      try {
        for (const id of [TARGET_POINT_LAYER_ID, SOURCE_POINT_LAYER_ID, LINK_LAYER_ID]) {
          if (map.getLayer(id)) map.removeLayer(id);
        }
        if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
      } catch {
        // Style may already be tearing down.
      }
    },
  };
}

function createCanvasSurface(container: HTMLElement): GeoreferenceDrawSurface | null {
  const adapter = getCanvasMapHostHandle(container);
  if (!adapter || typeof adapter.subscribePathProfilePicks !== 'function') return null;

  return {
    engine: 'openlayers',
    attach(handlers) {
      return (
        getCanvasMapHostHandle(container)?.subscribePathProfilePicks({
          onPoint: (lonLat) => handlers.onPoint(lonLat),
          onFinish: (lonLat) => handlers.onPoint(lonLat),
        }) ?? null
      );
    },
    render() {
      // Control-point glyphs stay on the MapLibre path; OL picking still works.
    },
    clear() {
      getCanvasMapHostHandle(container)?.clearPathProfileOverlay();
    },
  };
}

export function resolveGeoreferenceDrawSurface(
  container: HTMLElement | null | undefined,
): GeoreferenceDrawSurface | null {
  if (!container) return null;
  return createMapLibreSurface(container) ?? createCanvasSurface(container);
}

/**
 * Replace the tiled raster with a 4-corner image overlay so rotation/skew
 * from the affine actually moves the picture onto the earth.
 */
export function applyGeoreferenceImagePlacement(
  container: HTMLElement | null | undefined,
  layerId: string,
  previewUrl: string,
  corners: GeoreferenceCorners,
): boolean {
  if (!container) return false;
  const map = getMapLibreHostHandle(container);
  if (!map?.isStyleLoaded()) return false;

  const sourceId = `src-${layerId}`;
  const styleLayerId = `layer-${layerId}`;
  const coordinates: [[number, number], [number, number], [number, number], [number, number]] = corners.map(
    (corner) => [corner[0], corner[1]],
  ) as [[number, number], [number, number], [number, number], [number, number]];

  try {
    if (map.getLayer(styleLayerId)) map.removeLayer(styleLayerId);
    if (map.getSource(sourceId)) map.removeSource(sourceId);
    map.addSource(sourceId, {
      type: 'image',
      url: previewUrl,
      coordinates,
    });
    map.addLayer({
      id: styleLayerId,
      type: 'raster',
      source: sourceId,
      paint: { 'raster-opacity': 1, 'raster-fade-duration': 0 },
    });
    const xs = corners.map((corner) => corner[0]);
    const ys = corners.map((corner) => corner[1]);
    map.fitBounds(
      [
        [Math.min(...xs), Math.min(...ys)],
        [Math.max(...xs), Math.max(...ys)],
      ],
      { padding: 48, maxZoom: 18, duration: 700 },
    );
    return true;
  } catch (err) {
    console.warn('[georeference] image placement failed', err);
    return false;
  }
}
